import React from 'react';
import { LinkIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
import { ReportHero } from '../components/home/ReportHero';
import { KeyFindings } from '../components/home/KeyFindings';
import { ResearchSection } from '../components/home/ResearchSection';
import { FrameworkTimeline } from '../components/FrameworkTimeline';
import { EcosystemDiagram } from '../components/EcosystemDiagram';
import { ReviewCard } from '../components/Cards';
import { Button } from '../components/Button';
import { REVIEWS } from '../data/site';
const SOURCES = ['U.S. Energy Information Administration — Residential Energy Consumption Survey', 'Manufacturer specification sheets and published warranty terms', 'Independent laboratory certification and contaminant-reduction data', 'Agreexa Trust Framework® scoring records and editorial review logs', 'Field measurements against calibrated reference instruments'];
export function Home() {
  useScreenInit();
  return <article className="w-full bg-paper" data-id="element-298">
      <ReportHero data-id="element-299" />
      <KeyFindings data-id="element-300" />

      {/* Investigation sections */}
      <ResearchSection number="01" kicker="The Findings · Energy" title="Home battery storage has matured — but the spec sheets still mislead" paragraphs={['Across the systems we examined, the gap between advertised capacity and usable, real-world capacity remained the single most common source of buyer disappointment. Manufacturers routinely quote nameplate figures that assume ideal temperatures and brand-new cells.', 'When our analysts measured delivered energy over repeated cycles, the best systems held remarkably close to their claims. Others fell short by margins large enough to change which appliances a household could actually keep running during an outage.', 'The takeaway is not that storage has failed to mature — it clearly has — but that independent verification is now essential to separate durable engineering from confident marketing.']} stats={[{
      value: '92%',
      label: 'Usable vs. nameplate (top system)'
    }, {
      value: '3 mo.',
      label: 'Continuous field testing'
    }, {
      value: '96/100',
      label: 'Highest performance sub-score'
    }]} quote={{
      text: 'The most trustworthy products were the ones whose real numbers matched their marketing — and those were rarer than we expected.',
      attribution: 'Editorial Investigations Team'
    }} image={{
      src: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1200&q=80',
      caption: 'Battery storage systems assessed over repeated charge cycles.'
    }} data-id="element-301" />

      <ResearchSection number="02" kicker="The Findings · Water" title="Certified performance and marketed performance are not the same thing" paragraphs={['Water independence is often sold on dramatic contaminant-reduction claims. Our review compared those marketed claims against certified laboratory data for the same systems — and the discrepancies were instructive.', 'Several products performed exactly as certified. A handful leaned on selective figures that described best-case laboratory conditions unlikely to be reproduced at a household tap. We scored each system only on what could be independently substantiated.']} imageSide="left" image={{
      src: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?auto=format&fit=crop&w=1200&q=80',
      caption: 'Filtration systems benchmarked against certified reduction data.'
    }} stats={[{
      value: '14',
      label: 'Systems compared'
    }, {
      value: '100%',
      label: 'Claims cross-checked vs. certification'
    }, {
      value: '0',
      label: 'Unverified claims published'
    }]} data-id="element-302" />

      <ResearchSection number="03" kicker="The Findings · Preparedness" title="Resilience is built by planning — not by accumulating equipment" paragraphs={['The households best prepared for disruption were rarely those who had spent the most. They were the ones who had matched capability to genuine risk: power for the essentials, potable water, communications and a realistic two-week horizon.', 'Our reporting found that a great deal of preparedness spending is redundant — duplicate gear that never leaves the box. The most effective setups were deliberate, tested and periodically rehearsed.']} quote={{
      text: 'Preparedness is a practice, not a purchase. The gear only matters once it has been tested against a real plan.'
    }} data-id="element-303" />

      {/* Methodology — how we verified */}
      <section className="w-full bg-white border-t border-stone-200" data-id="element-304">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-24" data-id="element-305">
          <div className="flex items-baseline gap-4 mb-5" data-id="element-306">
            <span className="font-serif text-2xl text-stone-300 tracking-tightest" style={{
            fontWeight: 500
          }} data-id="element-307">
              04
            </span>
            <span className="eyebrow text-trust-600" data-id="element-308">
              Methodology · How We Verified
            </span>
          </div>
          <h2 className="font-serif tracking-tightest text-ink text-3xl md:text-4xl leading-[1.08] max-w-3xl" style={{
          fontWeight: 500
        }} data-id="element-309">
            Every claim in this report passed the Agreexa Trust Framework®
          </h2>
          <p className="mt-6 text-[1.075rem] leading-relaxed text-stone-700 max-w-2xl" data-id="element-310">
            Nothing reaches publication until it has moved through a consistent,
            auditable process — researched, analysed, verified, scored and
            editorially approved.
          </p>
          <div className="mt-14 max-w-3xl" data-id="element-311">
            <FrameworkTimeline data-id="element-312" />
          </div>
        </div>
      </section>

      {/* Referenced evidence — reviews */}
      <section className="w-full bg-paper border-t border-stone-200" data-id="element-313">
        <div className="max-w-editorial mx-auto px-6 py-16 md:py-24" data-id="element-314">
          <div className="flex items-baseline gap-4 mb-10" data-id="element-315">
            <span className="font-serif text-2xl text-stone-300 tracking-tightest" style={{
            fontWeight: 500
          }} data-id="element-316">
              05
            </span>
            <span className="eyebrow text-trust-600" data-id="element-317">
              The Evidence · Verified Reviews
            </span>
          </div>
          <div className="grid gap-8 md:grid-cols-3" data-id="element-318">
            {REVIEWS.map(r => <ReviewCard key={r.slug} review={r} data-id="element-319" />)}
          </div>
          <div className="mt-10" data-id="element-320">
            <Button to="/reviews" variant="secondary" data-id="element-321">
              Read every verified review
            </Button>
          </div>
        </div>
      </section>

      {/* Sources */}
      <section className="w-full bg-white border-t border-stone-200" data-id="element-322">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20" data-id="element-323">
          <p className="eyebrow text-trust-600 mb-6" data-id="element-324">
            Sources &amp; References
          </p>
          <ol className="space-y-3" data-id="element-325">
            {SOURCES.map((s, i) => <li key={i} className="flex items-start gap-3 text-sm text-stone-600" data-id="element-326">
                <span className="font-serif text-stone-400 tabular-nums" style={{
              fontWeight: 500
            }} data-id="element-327">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="flex items-start gap-1.5" data-id="element-328">
                  <LinkIcon className="h-3.5 w-3.5 mt-0.5 text-stone-400 shrink-0" data-id="element-329" />
                  {s}
                </span>
              </li>)}
          </ol>
        </div>
      </section>

      {/* About the publisher — ecosystem */}
      <section className="w-full bg-paper border-t border-stone-200" data-id="element-330">
        <div className="max-w-editorial mx-auto px-6 py-16 md:py-24" data-id="element-331">
          <div className="max-w-2xl mb-14" data-id="element-332">
            <p className="eyebrow text-trust-600 mb-5" data-id="element-333">About the Publisher</p>
            <h2 className="font-serif tracking-tightest text-ink text-3xl md:text-4xl leading-[1.08]" style={{
            fontWeight: 500
          }} data-id="element-334">
              An independent report from an Agreexa® publication
            </h2>
            <p className="mt-6 text-[1.075rem] leading-relaxed text-stone-700" data-id="element-335">
              Home Independence Hub is the flagship publication built on
              Agreexa’s editorial trust infrastructure — the standard that
              governs how every recommendation across the ecosystem is
              researched and verified.
            </p>
          </div>
          <EcosystemDiagram data-id="element-336" />
        </div>
      </section>
    </article>;
}